import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  IconButton,
  TextField,
  FormControlLabel,
  Checkbox,
  Select,
  MenuItem,
  Skeleton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import ButtonComponent from '../ui/ButtonComponent';
import { getAuth } from 'firebase/auth';

// ----------------------------------------------------------------------
// Reusable "Edit Annotation" Modal
// ----------------------------------------------------------------------
const EditAnnotationModal = ({ open, onClose, isSubmitting, data, setData, onConfirm, title }) => {
  return (
    <Dialog
      open={open}
      onClose={isSubmitting ? undefined : onClose}
      fullWidth
      maxWidth="sm"
      sx={{ "& .MuiDialog-paper": { height: "auto", maxHeight: '80vh' } }}
    >
      <DialogTitle>{title || "Edit Annotation"}</DialogTitle>

      <DialogContent dividers sx={{ display: "flex", flexDirection: "column", gap: 2, p: 3 }}>
        <Box>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                Annotation Type
            </Typography>
            <Select
            value={data.section}
            onChange={(e) => setData({ ...data, section: e.target.value })}
            fullWidth
            size="small"
            disabled={data.editIndex !== null} 
            >
            <MenuItem value="headers">Header</MenuItem>
            <MenuItem value="labels">Label</MenuItem>
            <MenuItem value="standardNotations">Standard Notation</MenuItem>
            </Select>
        </Box>

        {data.section === 'standardNotations' && (
            <Box display="flex" gap={2}>
            <Box flex={1}>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                    Category
                </Typography>
                <Select
                value={data.category}
                onChange={(e) => setData({ ...data, category: e.target.value })}
                fullWidth
                size="small"
                >
                <MenuItem value="Orientation">Orientation</MenuItem>
                <MenuItem value="Exit">Exit</MenuItem>
                </Select>
            </Box>
            <Box flex={1}>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                    Subsection
                </Typography>
                <Select
                value={data.subsection}
                onChange={(e) => setData({ ...data, subsection: e.target.value })}
                fullWidth
                size="small"
                >
                <MenuItem value="Frontal Poles">Frontal Poles</MenuItem>
                <MenuItem value="Temporal Lobes">Temporal Lobes</MenuItem>
                </Select>
            </Box>
            </Box>
        )}

        <TextField
            label="Title / Label"
            value={data.title}
            onChange={(e) => setData({ ...data, title: e.target.value })}
            fullWidth
            size="small"
            disabled={data.section === 'labels'}
            helperText={data.section === 'labels' ? "Labels do not have titles." : ""}
        />

        <FormControlLabel
            control={
            <Checkbox
                checked={data.includeTitle}
                onChange={(e) => setData({ ...data, includeTitle: e.target.checked })}
                disabled={data.section === 'labels'}
            />
            }
            label={
                <Typography variant="body2" color="text.secondary">
                    Include Title/Label in PDF Annotation
                </Typography>
            }
        />

        <TextField
            label="Annotation Text"
            value={data.text}
            onChange={(e) => setData({ ...data, text: e.target.value })}
            fullWidth
            multiline
            rows={4}
            placeholder="Enter the content of the annotation..."
        />
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <ButtonComponent
          label="Cancel"
          type="secondary"
          onClick={onClose}
          disabled={isSubmitting}
        />
        <ButtonComponent
          label={data.editIndex !== null ? "Save Changes" : "Create Annotation"}
          type="primary"
          onClick={onConfirm}
          isLoading={isSubmitting}
          disabled={!data.text || isSubmitting}
        />
      </DialogActions>
    </Dialog>
  );
};

// ----------------------------------------------------------------------
// Main AnnotationLibrary Component
// ----------------------------------------------------------------------
const AnnotationLibrary = ({ onSelectAnnotation }) => {
  const [headers, setHeaders] = useState([]);
  const [labels, setLabels] = useState([]);
  const [standardNotations, setStandardNotations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [modalData, setModalData] = useState({
    section: 'headers',
    title: '',
    text: '',
    includeTitle: true,
    editIndex: null,
    category: 'Orientation',
    subsection: 'Frontal Poles',
  });

  const fetchLibraryFromCloudFunction = async () => {
    setLoading(true);
    try {
      const user = getAuth().currentUser;
      if (!user) return;
      const idToken = await user.getIdToken();
      const idTokenResult = await user.getIdTokenResult();
      
      // SAFELY EXTRACT ORGID FROM CLAIMS
      const orgId = idTokenResult.claims.organizationId || idTokenResult.claims.orgId;
      if (!orgId) throw new Error("Organization ID not found in token claims.");

      const response = await fetch('https://loadannotationlibrary-774073159618.us-central1.run.app', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${idToken}`,
          'Organization-ID': orgId
        },
        body: JSON.stringify({})
      });

      if (!response.ok) throw new Error('Failed to load annotation library');
      const data = await response.json();

      setHeaders(data.headers || []);
      setLabels(data.labels || []);
      setStandardNotations((data.standardNotations || []).map(n => ({
        ...n,
        category: n.category || 'Uncategorized',
        subsection: n.subsection || 'Uncategorized'
      })));
    } catch (error) {
      console.error('Error loading annotation library:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveLibraryToCloudFunction = async (updated) => {
    try {
      const user = getAuth().currentUser;
      if (!user) return;
      const idToken = await user.getIdToken();
      const idTokenResult = await user.getIdTokenResult();
      
      // SAFELY EXTRACT ORGID FROM CLAIMS
      const orgId = idTokenResult.claims.organizationId || idTokenResult.claims.orgId;
      if (!orgId) throw new Error("Organization ID not found in token claims.");

      await fetch('https://saveannotationslibrary-774073159618.us-central1.run.app', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${idToken}`,
          'Organization-ID': orgId
        },
        body: JSON.stringify(updated)
      });
    } catch (error) {
      console.error('Error saving annotation library:', error);
    }
  };

  useEffect(() => {
    fetchLibraryFromCloudFunction();
  }, []);

  const handleModalConfirm = async () => {
    setIsSubmitting(true);
    const { section, title, text, includeTitle, editIndex, category, subsection } = modalData;
    
    const update = (setter, current, payloadBuilder) => {
      const updated = [...current];
      const payload = payloadBuilder();
      if (editIndex !== null) {
        updated[editIndex] = payload;
      } else {
        updated.push(payload);
      }
      setter(updated);
      return updated;
    };

    let newHeaders = headers, newLabels = labels, newNotations = standardNotations;

    if (section === 'headers') {
      newHeaders = update(setHeaders, headers, () => ({ title, text, includeTitle }));
    } else if (section === 'labels') {
      newLabels = update(setLabels, labels, () => ({ text, includeTitle }));
    } else if (section === 'standardNotations') {
      newNotations = update(setStandardNotations, standardNotations, () => ({
        label: title,
        text,
        includeTitle,
        category,
        subsection
      }));
    }

    await saveLibraryToCloudFunction({
      headers: newHeaders,
      labels: newLabels,
      standardNotations: newNotations,
    });

    await fetchLibraryFromCloudFunction(); 
    setIsSubmitting(false);
    setModalOpen(false);
  };

  const handleDelete = async (section, index) => {
    if (!window.confirm("Are you sure you want to delete this annotation?")) return;

    let updatedHeaders = headers, updatedLabels = labels, updatedNotations = standardNotations;

    if (section === 'headers') {
      updatedHeaders = [...headers];
      updatedHeaders.splice(index, 1);
      setHeaders(updatedHeaders);
    } else if (section === 'labels') {
      updatedLabels = [...labels];
      updatedLabels.splice(index, 1);
      setLabels(updatedLabels);
    } else if (section === 'standardNotations') {
      updatedNotations = [...standardNotations];
      updatedNotations.splice(index, 1);
      setStandardNotations(updatedNotations);
    }

    await saveLibraryToCloudFunction({
      headers: updatedHeaders,
      labels: updatedLabels,
      standardNotations: updatedNotations,
    });
    fetchLibraryFromCloudFunction(); 
  };

  const openAddModal = () => {
    setModalData({
      section: 'headers',
      title: '',
      text: '',
      includeTitle: true,
      editIndex: null,
      category: 'Orientation',
      subsection: 'Frontal Poles',
    });
    setModalOpen(true);
  };

  const openEditModal = (section, item, index, e) => {
    e.stopPropagation();
    setModalData({
      section,
      title: item.title || item.label || '',
      text: item.text || '',
      includeTitle: item.includeTitle ?? true,
      editIndex: index,
      category: item.category || 'Uncategorized',
      subsection: item.subsection || 'Uncategorized',
    });
    setModalOpen(true);
  };

  const handleSelect = (item, section) => {
    const includeTitle = item.includeTitle ?? true;

    // DATE ASSIGNMENT DELEGATED TO VIEWER: 
    // Simply fire off the label block and let MultiPagePDFViewer append the extracted date automatically
    if (section === 'headers') {
      onSelectAnnotation({ 
        ...item, 
        type: 'header', 
        slot: 'header', 
        includeTitle 
      });
    } else if (section === 'labels') {
      onSelectAnnotation({ 
        type: 'label', 
        title: null, 
        text: item.text, 
        slot: 'label' 
      });
    } else if (section === 'standardNotations') {
      onSelectAnnotation({
        type: 'standardNotation', 
        title: item.label,
        text: item.text,
        slot: 'notationA',
        includeTitle,
      });
    }
  };

  const renderListItems = (items, section) => (
    <List dense>
      {items.map((item, idx) => {
        const display = item.title || item.label || item.text || '(Untitled)';
        return (
          <ListItem
            key={idx}
            secondaryAction={
              <>
                <IconButton onClick={(e) => openEditModal(section, item, idx, e)} edge="end">
                  <EditIcon fontSize="small" />
                </IconButton>
                <IconButton onClick={(e) => { e.stopPropagation(); handleDelete(section, idx); }} edge="end">
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </>
            }
            disablePadding
          >
            <ListItemButton onClick={() => handleSelect(item, section)}>
              <ListItemText 
                primary={display} 
                primaryTypographyProps={{ 
                    fontSize: '0.875rem', 
                    style: { whiteSpace: 'normal', wordBreak: 'break-word' } 
                }} 
              />
            </ListItemButton>
          </ListItem>
        );
      })}
    </List>
  );

  const groupedNotations = standardNotations.reduce((acc, item) => {
    const cat = item.category || 'Uncategorized';
    const sub = item.subsection || 'Uncategorized';
    acc[cat] = acc[cat] || {};
    acc[cat][sub] = acc[cat][sub] || [];
    acc[cat][sub].push(item);
    return acc;
  }, {});

  const renderSkeletons = () => (
    <Box>
        {[1, 2, 3].map((i) => (
            <Skeleton key={i} variant="rectangular" height={48} sx={{ mb: 1, borderRadius: 1 }} />
        ))}
    </Box>
  );

  return (
    <Box 
      sx={{ 
        width: '100%', 
        height: '100%', 
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      <Typography variant="h6" gutterBottom sx={{ px: 2, pt: 2 }}>Annotation Library</Typography>

      {loading ? (
        <Box sx={{ px: 2 }}>{renderSkeletons()}</Box>
      ) : (
        <Box sx={{ flex: 1, overflowY: 'auto', px: 2 }}>
          <Accordion defaultExpanded disableGutters>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle2">Headers</Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 0 }}>{renderListItems(headers, 'headers')}</AccordionDetails>
          </Accordion>

          <Accordion disableGutters>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle2">Labels</Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 0 }}>{renderListItems(labels, 'labels')}</AccordionDetails>
          </Accordion>

          <Accordion disableGutters>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle2">Standard Notations</Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 0 }}>
              {Object.entries(groupedNotations).map(([category, sections]) => (
                <Accordion key={category} disableGutters elevation={0} sx={{ '&:before': { display: 'none' } }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 40, flexDirection: 'row-reverse', '& .MuiAccordionSummary-content': { ml: 1 } }}>
                    <Typography variant="body2" fontWeight={500}>{category}</Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ p: 0, pl: 2 }}>
                    {Object.entries(sections).map(([section, items]) => (
                      <Accordion key={section} disableGutters elevation={0} sx={{ '&:before': { display: 'none' } }}>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 32 }}>
                          <Typography variant="caption">{section}</Typography>
                        </AccordionSummary>
                        <AccordionDetails sx={{ p: 0 }}>
                          {renderListItems(items, 'standardNotations')}
                        </AccordionDetails>
                      </Accordion>
                    ))}
                  </AccordionDetails>
                </Accordion>
              ))}
            </AccordionDetails>
          </Accordion>
        </Box>
      )}

      <Box sx={{ p: 2, borderTop: '1px solid #eee', flexShrink: 0 }}>
        <ButtonComponent
            label="Add New Annotation"
            type="outlined"
            icon={<AddIcon />}
            onClick={openAddModal}
            fullWidth
        />
      </Box>

      <EditAnnotationModal 
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        isSubmitting={isSubmitting}
        data={modalData}
        setData={setModalData}
        onConfirm={handleModalConfirm}
        title={modalData.editIndex !== null ? 'Edit Annotation' : 'Add New Annotation'}
      />
    </Box>
  );
};

export default AnnotationLibrary;